import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Building2, MapPin, Save } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { ConfigurationEditor } from "@/components/ConfigurationEditor";
import { usePartnerScope } from "@/components/partner-scope-context";
import { ApplicationsTab } from "@/components/ApplicationsTab";
import { BrandEditor } from "@/components/BrandEditor";
import { ProvidersTab } from "@/components/ProvidersTab";
import { ScopedCredentialsPanel } from "@/components/ScopedCredentialsPanel";
import { VerticalsTab } from "@/components/VerticalsTab";
import { TechnicalDetails } from "@/components/TechnicalDetails";
import { usePageTitle } from "@/lib/use-page-title";
import { apiClient } from "@/lib/api";
import { describe } from "@/lib/api-error";
import { hasWriteOnOrg } from "@/lib/roles";
import { useRoleProfile } from "@/lib/use-role-profile";
import { cn } from "@/lib/utils";
import { validateEmail, validatePhone } from "@/lib/validation";
import { ApiError, type Organization } from "@tilt/admin-sdk";

import { ScopedPeopleTab } from "@/components/ScopedPeopleTab";

/**
 * /organizations/:id - drill-down for a single org.
 *
 * Three tabs: Overview (editable contact fields), Configuration
 * (JsonConfigEditor over organization.configuration JSONB), and
 * Locations (children, read-only list - full CRUD lives at
 * /locations).
 */
type TabKey =
  | "overview"
  | "branding"
  | "verticals"
  | "configuration"
  | "applications"
  | "providers"
  | "credentials"
  | "locations"
  | "users";

export function OrganizationDetailPage(): JSX.Element {
  usePageTitle("Organization");
  const { id = "" } = useParams<{ id: string }>();
  const { scope } = usePartnerScope();
  const [tab, setTab] = useState<TabKey>("overview");

  const org = useQuery({
    queryKey: ["organizations", "detail", id, scope.id],
    queryFn: () => apiClient.getOrganization(id),
    retry: false,
    enabled: !!id,
  });

  if (!id) return <p className="text-sm text-destructive">Missing organization id.</p>;

  if (org.isLoading) {
    return <p className="text-sm text-muted-foreground">Loading organization…</p>;
  }
  if (org.isError || !org.data) {
    const msg = org.error instanceof ApiError ? org.error.message : "Failed to load organization.";
    return <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{msg}</div>;
  }

  const data = org.data;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <Link
            to="/organizations"
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-3 w-3" /> All organizations
          </Link>
          <h1 className="mt-1 flex items-center gap-2 text-xl font-semibold tracking-tight sm:text-2xl">
            <Building2 className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
            {data.name}
          </h1>
          <p className="break-all font-mono text-xs text-muted-foreground">
            under {data.partner_path}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-1 border-b">
        <TabButton active={tab === "overview"} onClick={() => setTab("overview")}>
          Overview
        </TabButton>
        <TabButton active={tab === "branding"} onClick={() => setTab("branding")}>
          Branding
        </TabButton>
        <TabButton active={tab === "verticals"} onClick={() => setTab("verticals")}>
          Verticals
        </TabButton>
        <TabButton active={tab === "configuration"} onClick={() => setTab("configuration")}>
          Configuration
        </TabButton>
        <TabButton active={tab === "applications"} onClick={() => setTab("applications")}>
          Applications
        </TabButton>
        <TabButton active={tab === "providers"} onClick={() => setTab("providers")}>
          Providers
        </TabButton>
        <TabButton active={tab === "credentials"} onClick={() => setTab("credentials")}>
          Credentials
        </TabButton>
        <TabButton active={tab === "locations"} onClick={() => setTab("locations")}>
          Locations
        </TabButton>
        <TabButton active={tab === "users"} onClick={() => setTab("users")}>
          People
        </TabButton>
      </div>

      {tab === "overview" && <OverviewTab org={data} />}
      {tab === "branding" && <BrandingTab org={data} />}
      {tab === "verticals" && <VerticalsTab organizationId={data.id} />}
      {tab === "configuration" && <ConfigurationTab org={data} />}
      {tab === "applications" && <ApplicationsTab scope="organization" id={data.id} />}
      {tab === "providers" && (
        <ProvidersTab
          scope={{
            kind: "organization",
            id: data.id,
            name: data.name,
            partnerPath: data.partner_path,
          }}
        />
      )}
      {tab === "credentials" && (
        <ScopedCredentialsPanel
          partnerId={data.partner_id}
          partnerName={data.partner_name ?? "partner"}
          organizationId={data.id}
          organizationName={data.name}
        />
      )}
      {tab === "locations" && <LocationsTab orgId={data.id} />}
      {tab === "users" && (
        <ScopedPeopleTab
          presetScope={{
            scope_kind: "organization",
            scope_id: data.id,
            partner_id: data.partner_id,
            partner_path: data.partner_path,
            label: data.name,
          }}
        />
      )}

      <TechnicalDetails label="Technical details">
        <dl className="space-y-1 text-xs text-muted-foreground">
          <DetailRow label="id" value={data.id} mono />
          <DetailRow label="partner_id" value={data.partner_id} mono />
          <DetailRow label="partner_path" value={data.partner_path} mono />
          <DetailRow label="created_at" value={data.created_at} mono />
          {data.is_deleted && <DetailRow label="state" value="soft-deleted" />}
        </dl>
      </TechnicalDetails>
    </div>
  );
}

function TabButton(props: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={props.onClick}
      className={cn(
        "-mb-px border-b-2 px-3 py-1.5 text-sm",
        props.active
          ? "border-primary font-medium text-foreground"
          : "border-transparent text-muted-foreground hover:text-foreground",
      )}
    >
      {props.children}
    </button>
  );
}

function DetailRow(props: { label: string; value: string; mono?: boolean }): JSX.Element {
  return (
    <div className="flex flex-wrap items-baseline gap-2">
      <dt className="w-36 shrink-0 text-[11px] uppercase tracking-wide text-muted-foreground/70">
        {props.label}
      </dt>
      <dd className={cn("min-w-0 break-all", props.mono && "font-mono")}>{props.value}</dd>
    </div>
  );
}

function OverviewTab(props: { org: Organization }): JSX.Element {
  const { org } = props;
  const queryClient = useQueryClient();
  const profile = useRoleProfile();
  // Form mutations on this tab 403 without require_write on the
  // parent partner's path. Disable inputs + hide save button so
  // read-only admins see org details but can't trigger the mutation.
  const canWrite = hasWriteOnOrg(profile, org.id, org.partner_path);
  const [name, setName] = useState(org.name);
  const [contactName, setContactName] = useState(org.contact_name ?? "");
  const [contactEmail, setContactEmail] = useState(org.contact_email ?? "");
  const [contactPhone, setContactPhone] = useState(org.contact_phone ?? "");
  const [address, setAddress] = useState(org.address ?? "");
  const [logoUrl, setLogoUrl] = useState(org.logo_url ?? "");
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset local state when org changes (e.g. after save).
  useEffect(() => {
    setName(org.name);
    setContactName(org.contact_name ?? "");
    setContactEmail(org.contact_email ?? "");
    setContactPhone(org.contact_phone ?? "");
    setAddress(org.address ?? "");
    setLogoUrl(org.logo_url ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org.id, org.updated_at]);

  const emailCheck = validateEmail(contactEmail);
  const phoneCheck = validatePhone(contactPhone);
  const patch = useMutation({
    mutationFn: () => {
      if (!emailCheck.ok) throw new Error(emailCheck.error);
      if (!phoneCheck.ok) throw new Error(phoneCheck.error);
      return apiClient.patchOrganization(org.id, {
        name: name.trim(),
        contact_name: contactName.trim() || null,
        contact_email: contactEmail.trim() || null,
        contact_phone: contactPhone.trim() || null,
        address: address.trim() || null,
        logo_url: logoUrl.trim() || null,
      });
    },
    onSuccess: () => {
      setError(null);
      setSaved(true);
      void queryClient.invalidateQueries({ queryKey: ["organizations"] });
      setTimeout(() => setSaved(false), 2000);
    },
    onError: (err: unknown) => setError(describe(err)),
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        patch.mutate();
      }}
      className="space-y-3 rounded-lg border bg-card p-4 shadow-sm sm:p-5"
    >
      {!canWrite && (
        <div className="rounded-md border border-dashed bg-muted/30 p-2 text-[11px] text-muted-foreground">
          Read-only - you don't have write access on{" "}
          <span className="font-mono">{org.partner_path}</span>.
        </div>
      )}
      <Field label="Name" id="org-name">
        <input
          id="org-name"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={!canWrite}
          className="w-full rounded-md border bg-background px-3 py-2 text-sm disabled:opacity-60"
        />
      </Field>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Contact name" id="org-cname">
          <input
            id="org-cname"
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            disabled={!canWrite}
            className="w-full rounded-md border bg-background px-3 py-2 text-sm disabled:opacity-60"
          />
        </Field>
        <Field label="Contact email" id="org-cemail">
          <input
            id="org-cemail"
            type="email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            aria-invalid={!emailCheck.ok}
            disabled={!canWrite}
            className={cn(
              "w-full rounded-md border bg-background px-3 py-2 text-sm disabled:opacity-60",
              !emailCheck.ok && "border-destructive",
            )}
          />
          {!emailCheck.ok && (
            <p className="mt-1 text-[11px] text-destructive">{emailCheck.error}</p>
          )}
        </Field>
        <Field label="Contact phone" id="org-cphone">
          <input
            id="org-cphone"
            type="tel"
            value={contactPhone}
            onChange={(e) => setContactPhone(e.target.value)}
            aria-invalid={!phoneCheck.ok}
            disabled={!canWrite}
            className={cn(
              "w-full rounded-md border bg-background px-3 py-2 text-sm disabled:opacity-60",
              !phoneCheck.ok && "border-destructive",
            )}
          />
          {!phoneCheck.ok && (
            <p className="mt-1 text-[11px] text-destructive">{phoneCheck.error}</p>
          )}
        </Field>
        <Field label="Logo URL" id="org-logo">
          <input
            id="org-logo"
            type="url"
            value={logoUrl}
            onChange={(e) => setLogoUrl(e.target.value)}
            disabled={!canWrite}
            className="w-full rounded-md border bg-background px-3 py-2 text-sm disabled:opacity-60"
          />
        </Field>
      </div>
      <Field label="Address" id="org-addr">
        <input
          id="org-addr"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          disabled={!canWrite}
          className="w-full rounded-md border bg-background px-3 py-2 text-sm disabled:opacity-60"
        />
      </Field>

      {error && (
        <div className="rounded-md bg-destructive/10 p-2 text-xs text-destructive">{error}</div>
      )}
      {canWrite && (
        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={patch.isPending}
            className="flex items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            <Save className="h-4 w-4" /> {patch.isPending ? "Saving…" : "Save"}
          </button>
          {saved && <span className="text-xs text-primary">Saved.</span>}
        </div>
      )}
    </form>
  );
}

function Field(props: { label: string; id: string; children: React.ReactNode }): JSX.Element {
  return (
    <div>
      <label htmlFor={props.id} className="mb-1 block text-xs font-medium text-muted-foreground">
        {props.label}
      </label>
      {props.children}
    </div>
  );
}

function ConfigurationTab(props: { org: Organization }): JSX.Element {
  const { org } = props;
  const queryClient = useQueryClient();
  const profile = useRoleProfile();
  const canWrite = hasWriteOnOrg(profile, org.id, org.partner_path);

  return (
    <div className="space-y-3">
      <ConfigurationEditor
        tier="organization"
        value={(org.configuration ?? {}) as Record<string, unknown>}
        canWrite={canWrite}
        onSave={async (next) => {
          await apiClient.patchOrganization(org.id, { configuration: next });
          await queryClient.invalidateQueries({ queryKey: ["organizations"] });
        }}
      />
      {!canWrite && (
        <p className="text-[11px] text-muted-foreground">
          Read-only - you don&apos;t have write access on{" "}
          <span className="font-mono">{org.partner_path}</span>.
        </p>
      )}
    </div>
  );
}

function LocationsTab(props: { orgId: string }): JSX.Element {
  const { scope } = usePartnerScope();
  const list = useQuery({
    queryKey: ["locations", scope.id, { organization_id: props.orgId }],
    queryFn: () => apiClient.listLocations({ organization_id: props.orgId }),
    retry: false,
  });

  if (list.isLoading) return <p className="text-sm text-muted-foreground">Loading locations…</p>;
  if (list.isError) {
    const msg = list.error instanceof Error ? list.error.message : "Failed to load locations";
    return <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{msg}</div>;
  }
  const locations = list.data?.locations.filter((l) => !l.is_deleted) ?? [];

  return (
    <div className="space-y-2">
      {locations.length === 0 ? (
        <div className="rounded-lg border border-dashed bg-muted/40 p-6 text-center text-sm text-muted-foreground">
          No locations yet.{" "}
          <Link
            to={`/locations?organization_id=${encodeURIComponent(props.orgId)}&create=1`}
            className="text-primary hover:underline"
          >
            Create one →
          </Link>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border bg-card">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/40 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-2.5">Name</th>
                <th className="px-4 py-2.5">Slug</th>
                <th className="px-4 py-2.5">Vertical</th>
                <th className="px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {locations.map((l) => (
                <tr key={l.id} className="border-b last:border-b-0 hover:bg-muted/30">
                  <td className="px-4 py-2.5">
                    <span className="font-medium">{l.name}</span>
                  </td>
                  <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">{l.slug}</td>
                  <td className="px-4 py-2.5 text-xs text-muted-foreground">
                    {l.vertical_display_name ?? l.vertical_slug ?? "-"}
                  </td>
                  <td className="px-4 py-2.5">
                    <LocationStatusPill status={l.status} />
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <Link
                      to={`/locations/${l.id}`}
                      className="inline-flex items-center gap-1 rounded-md border bg-background px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
                    >
                      <MapPin className="h-3 w-3" /> Open
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function LocationStatusPill(props: { status: string }): JSX.Element {
  const active = props.status === "active";
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium",
        active ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground",
      )}
    >
      {props.status}
    </span>
  );
}

// V0023 - org-level whitelabel override. Empty draft fields inherit
// from the parent partner; placeholder text surfaces what will be
// applied if left blank.
function BrandingTab(props: { org: Organization }): JSX.Element {
  const queryClient = useQueryClient();
  const profile = useRoleProfile();
  const canWrite = hasWriteOnOrg(profile, props.org.id, props.org.partner_path);
  const parentPartner = useQuery({
    queryKey: ["partner", props.org.partner_id],
    queryFn: () => apiClient.getPartner(props.org.partner_id),
    enabled: !!props.org.partner_id,
    retry: false,
  });
  return (
    <div className="space-y-3">
      <BrandEditor
        scope={{ kind: "organization", id: props.org.id }}
        title="Organization branding"
        value={props.org.brand_config ?? {}}
        disabled={!canWrite}
        inheritedFrom={parentPartner.data?.brand_config ?? null}
        inheritedLabel={parentPartner.data?.name ?? "parent partner"}
        onSave={async (next) => {
          await apiClient.patchOrganization(props.org.id, { brand_config: next });
          void queryClient.invalidateQueries({ queryKey: ["organizations"] });
          void queryClient.invalidateQueries({ queryKey: ["organizations", "detail"] });
        }}
      />
    </div>
  );
}
